import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useSettings } from "../../contexts/SettingsContext";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const ICON_OPTIONS = ["instagram", "twitter", "facebook", "youtube", "linkedin"];

const emptyLink = { label_en: "", label_nl: "", href: "/" };
const emptyColumn = { title_en: "Column", title_nl: "Kolom", links: [] };
const emptySocial = { label: "Instagram", href: "https://instagram.com/snapshop", icon: "instagram" };

const AdminFooter = () => {
  const { lang } = useI18n();
  const { refresh } = useSettings();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/admin/settings").then((r) => setF(r.data?.footer || {})); }, []);

  if (!f) return <div className="text-ink-muted">Loading…</div>;

  const upd = (k, v) => setF({ ...f, [k]: v });
  const updColumn = (ci, k, v) => setF({ ...f, columns: (f.columns || []).map((c, idx) => idx === ci ? { ...c, [k]: v } : c) });
  const updLink = (ci, li, k, v) => setF({
    ...f,
    columns: (f.columns || []).map((c, idx) => idx === ci ? { ...c, links: (c.links || []).map((ln, jdx) => jdx === li ? { ...ln, [k]: v } : ln) } : c),
  });
  const addColumn = () => upd("columns", [...(f.columns || []), { ...emptyColumn, links: [{ ...emptyLink }] }]);
  const removeColumn = (ci) => upd("columns", (f.columns || []).filter((_, idx) => idx !== ci));
  const moveColumn = (ci, dir) => {
    const cols = [...(f.columns || [])];
    const j = ci + dir;
    if (j < 0 || j >= cols.length) return;
    [cols[ci], cols[j]] = [cols[j], cols[ci]];
    upd("columns", cols);
  };
  const addLink = (ci) => updColumn(ci, "links", [...(f.columns?.[ci]?.links || []), { ...emptyLink }]);
  const removeLink = (ci, li) => updColumn(ci, "links", (f.columns?.[ci]?.links || []).filter((_, idx) => idx !== li));

  const updSocial = (i, k, v) => upd("social", (f.social || []).map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addSocial = () => upd("social", [...(f.social || []), { ...emptySocial }]);
  const removeSocial = (i) => upd("social", (f.social || []).filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put("/admin/settings", { footer: f });
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-footer-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Footer" : "Footer"}</h1>
          <div className="font-label text-ink-muted mt-1">{lang === "nl" ? "Bewerk ribbon, linkkolommen en social media" : "Edit ribbon, link columns and social media"}</div>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="footer-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
      </div>

      {/* Ribbon + Newsletter blurb */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Bovenste ribbon" : "Top ribbon"}</div>
          <F label="Ribbon NL" value={f.ribbon_nl} onChange={(v) => upd("ribbon_nl", v)} testid="footer-ribbon-nl" />
          <F label="Ribbon EN" value={f.ribbon_en} onChange={(v) => upd("ribbon_en", v)} testid="footer-ribbon-en" />
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Nieuwsbrief tekst" : "Newsletter blurb"}</div>
          <Textarea label="Blurb NL" value={f.newsletter_blurb_nl} onChange={(v) => upd("newsletter_blurb_nl", v)} testid="footer-blurb-nl" />
          <Textarea label="Blurb EN" value={f.newsletter_blurb_en} onChange={(v) => upd("newsletter_blurb_en", v)} testid="footer-blurb-en" />
        </div>
      </div>

      {/* Link columns */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Linkkolommen" : "Link columns"}</div>
          <button onClick={addColumn} className="btn-secondary text-xs" data-testid="footer-add-column"><Plus className="w-3.5 h-3.5" /> {lang === "nl" ? "Kolom" : "Column"}</button>
        </div>
        <div className="space-y-4">
          {(f.columns || []).map((col, ci) => (
            <div key={ci} className="border border-black/10 bg-white p-5" data-testid={`footer-column-${ci}`}>
              <div className="grid md:grid-cols-[1fr_1fr_140px] gap-3 items-end mb-3">
                <F label="Title EN" value={col.title_en} onChange={(v) => updColumn(ci, "title_en", v)} testid={`footer-col-title-en-${ci}`} />
                <F label="Titel NL" value={col.title_nl} onChange={(v) => updColumn(ci, "title_nl", v)} testid={`footer-col-title-nl-${ci}`} />
                <div className="flex justify-end gap-1">
                  <button onClick={() => moveColumn(ci, -1)} className="p-2 hover:bg-secondary-warm" title="Up"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveColumn(ci, 1)} className="p-2 hover:bg-secondary-warm" title="Down"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeColumn(ci)} className="p-2 hover:bg-terracotta/10 text-terracotta" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="border-t border-black/5 pt-3 space-y-2">
                {(col.links || []).map((ln, li) => (
                  <div key={li} className="grid grid-cols-[1fr_1fr_1.5fr_40px] gap-2 items-center" data-testid={`footer-link-${ci}-${li}`}>
                    <input value={ln.label_en} onChange={(e) => updLink(ci, li, "label_en", e.target.value)} placeholder="Label EN" className="border border-black/15 px-2.5 py-1.5 text-sm bg-white" />
                    <input value={ln.label_nl} onChange={(e) => updLink(ci, li, "label_nl", e.target.value)} placeholder="Label NL" className="border border-black/15 px-2.5 py-1.5 text-sm bg-white" />
                    <input value={ln.href} onChange={(e) => updLink(ci, li, "href", e.target.value)} placeholder="/legal/privacy" className="border border-black/15 px-2.5 py-1.5 text-sm bg-white font-mono" />
                    <button onClick={() => removeLink(ci, li)} className="p-1.5 hover:bg-terracotta/10 text-terracotta"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => addLink(ci)} className="btn-secondary text-xs" data-testid={`footer-add-link-${ci}`}><Plus className="w-3.5 h-3.5" /> {lang === "nl" ? "Link" : "Link"}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Social */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Social media" : "Social media"}</div>
          <button onClick={addSocial} className="btn-secondary text-xs" data-testid="footer-add-social"><Plus className="w-3.5 h-3.5" /> Social</button>
        </div>
        <div className="border border-black/10 bg-white p-5 space-y-2">
          {(f.social || []).map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1.5fr_40px] gap-2 items-center" data-testid={`footer-social-row-${i}`}>
              <input value={s.label} onChange={(e) => updSocial(i, "label", e.target.value)} placeholder="Instagram" className="border border-black/15 px-2.5 py-1.5 text-sm bg-white" />
              <select value={s.icon} onChange={(e) => updSocial(i, "icon", e.target.value)} className="border border-black/15 px-2.5 py-1.5 text-sm bg-white">
                {ICON_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <input value={s.href} onChange={(e) => updSocial(i, "href", e.target.value)} placeholder="https://instagram.com/yourshop" className="border border-black/15 px-2.5 py-1.5 text-sm bg-white font-mono" />
              <button onClick={() => removeSocial(i)} className="p-1.5 hover:bg-terracotta/10 text-terracotta"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Legal + Badges */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Juridische regel" : "Legal line"}</div>
          <p className="text-xs text-ink-muted">{lang === "nl" ? "Gebruik {year}, {kvk}, {btw} als placeholders." : "Use {year}, {kvk}, {btw} as placeholders."}</p>
          <F label="Legal NL" value={f.legal_line_nl} onChange={(v) => upd("legal_line_nl", v)} testid="footer-legal-nl" />
          <F label="Legal EN" value={f.legal_line_en} onChange={(v) => upd("legal_line_en", v)} testid="footer-legal-en" />
        </div>
        <div className="border border-black/10 bg-white p-6 space-y-3">
          <div className="font-label text-ink-muted">{lang === "nl" ? "Betaalbadges" : "Payment badges"}</div>
          <p className="text-xs text-ink-muted">{lang === "nl" ? "Eén badge per regel." : "One badge per line."}</p>
          <textarea
            data-testid="footer-badges"
            rows={5}
            value={(f.payment_badges || []).join("\n")}
            onChange={(e) => upd("payment_badges", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
            className="border border-black/15 px-3 py-2 text-sm w-full font-mono bg-white"
          />
        </div>
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const Textarea = ({ label, value, onChange, testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <textarea data-testid={testid} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

export default AdminFooter;
